import Vue from 'vue'

import * as types from '../mutation-types'

import comments from '@/api/comments'
import users from '@/api/users'
import projects from '@/api/projects'
import posts from '@/api/posts'

const state = {
  comments: {},
  likes: {},
  projectComments: {},
  postComments: {},
  replies: {}
}

const getters = {
  likedComment: (state) => (id, username) => {
    return state.likes[id] ? state.likes[id][username] : false
  },

  projectComments: (state) => (id) => {
    if (!state.projectComments[id]) {
      return []
    }

    return state.projectComments[id].map((commentID) => { return state.comments[commentID] || null }).filter((comment) => { return !!comment })
  },

  postComments: (state) => (id) => {
    if (!state.postComments[id]) {
      return []
    }

    return state.postComments[id].map((commentID) => { return state.comments[commentID] || null }).filter((comment) => { return !!comment })
  },

  commentReplies: (state) => (id) => {
    if (!state.replies[id]) {
      return []
    }

    return state.replies[id].map((commentID) => { return state.comments[commentID] || null }).filter((comment) => { return !!comment })
  },
  attachmentsByComment: (state) => (commentID) => {
    return state.comments[commentID.attachments]
  }
}

const actions = {
  createComment ({ commit, state }, { projectID, postID, parentID, comment, anonymous, attachments }) {
    return new Promise((resolve, reject) => {
      let id = projectID
      let resource = projects
      if (postID) {
        id = postID
        resource = posts
      }

      let method = 'createComment'
      if (parentID) {
        method = 'createReply'
      }

      resource[method](id, comment, anonymous, attachments, parentID).then((response) => {
        const responseComment = response.data.result.comment
        commit(types.ADD_COMMENT, responseComment)

        if (responseComment.projectID) {
          commit(types.UPDATE_PROJECT_COMMENT_COUNT, { id: responseComment.projectID, count: 1 })
          if (!responseComment.parentID) {
            commit(types.ADD_PROJECT_COMMENT, responseComment)
          }
        }

        if (responseComment.postID) {
          commit(types.UPDATE_POST_COMMENT_COUNT, { id: responseComment.postID, count: 1 })
          commit(types.ADD_POST_COMMENT, responseComment)
        }

        if (response.data.result.autoSubscribed) {
          commit(types.SUBSCRIBE_PROJECT, { id: responseComment.projectID })
          commit(types.SUBSCRIBED_TO_PROJECT, { id: responseComment.projectID, username: responseComment.user.userName, subscribed: true })
        }
        resolve(responseComment)
      }, (error) => {
        reject(error)
      })
    })
  },
  getComment ({ commit }, { id }) {
    return new Promise((resolve, reject) => {
      comments.get(id).then((response) => {
        const responseComment = response.data.result.comment
        commit(types.ADD_COMMENT, responseComment)
        if (responseComment.projectID) {
          commit(types.UPDATE_PROJECT_COMMENT_COUNT, { id: responseComment.projectID, count: 1 })
          if (!responseComment.parentID) {
            commit(types.ADD_PROJECT_COMMENT, responseComment)
          }
        }

        if (responseComment.postID) {
          commit(types.UPDATE_POST_COMMENT_COUNT, { id: responseComment.postID, count: 1 })
          commit(types.ADD_POST_COMMENT, responseComment)
        }

        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateComment ({ commit }, { id, body, attachments }) {
    return new Promise((resolve, reject) => {
      comments.update(id, body, attachments).then((response) => {
        const responseComment = response.data.result.comment
        commit(types.ADD_COMMENT, responseComment)
        resolve(responseComment)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteComment ({ dispatch }, { commentID }) {
    return new Promise((resolve, reject) => {
      comments.delete(commentID).then(() => {
        dispatch('deleteLocalComment', commentID)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteLocalComment ({ commit, state }, id) {
    const comment = state.comments[id]
    if (!comment) {
      return
    }
    commit(types.DELETE_COMMENT, { id })
    commit(types.UPDATE_PROJECT_COMMENT_COUNT, { id: comment.projectID, count: -1 })
    commit(types.UPDATE_POST_COMMENT_COUNT, { id: comment.postID, count: -1 })
    const children = state.replies[comment.id]
    if (children?.length > 0) {
      commit(types.UPDATE_PROJECT_COMMENT_COUNT, { id: comment.projectID, count: -1 * children.length })
      commit(types.UPDATE_POST_COMMENT_COUNT, { id: comment.postID, count: -1 * children.length })
    } else {
      commit(types.UPDATE_COMMENT_REPLY_COUNT, { id: comment.parentID, count: -1 })
    }
  },
  getProjectComments ({ commit, state }, { id, limit, offset, socket }) {
    let before = 0
    let since = 0
    if (!offset && state.projectComments[id]) {
      if (socket) {
        since = state.projectComments[id][state.projectComments[id].length - 1]
      } else {
        before = state.projectComments[id][0]
      }
    }

    return new Promise((resolve, reject) => {
      projects.getComments(id, limit, offset, before, since).then((response) => {
        const comments = response.data.result.comments
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        comments.forEach((comment) => {
          commit(types.ADD_COMMENT, comment)

          if (comment.projectID) {
            commit(types.UPDATE_PROJECT_COMMENT_COUNT, { id: comment.projectID, count: 1 })
            if (!comment.parentID) {
              commit(types.ADD_PROJECT_COMMENT, comment)
            }
          }

          if (comment.postID) {
            commit(types.UPDATE_POST_COMMENT_COUNT, { id: comment.postID, count: 1 })
            commit(types.ADD_POST_COMMENT, comment)
          }
        })
        resolve({ comments, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPostComments ({ commit, state }, { id, limit, offset, socket }) {
    let before = 0
    let since = 0
    if (offset !== 0 && !offset && state.postComments[id]) {
      if (socket) {
        since = state.postComments[id][0]
      } else {
        before = state.postComments[id][state.postComments[id].length - 1]
      }
    }

    return new Promise((resolve, reject) => {
      posts.getComments(id, limit, offset, before, since).then((response) => {
        const comments = response.data.result.comments
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        comments.forEach((comment) => {
          commit(types.ADD_COMMENT, comment)
          commit(types.ADD_POST_COMMENT, comment)
        })
        if (socket) {
          commit(types.UPDATE_POST_COMMENT_COUNT, { id, count: comments.length })
        }
        resolve({ comments, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  cacheLikedComments ({ commit }, username) {
    if (state.likes.cached && state.likes.cached[username]) {
      return
    }

    users.getLikedCommentIDs(username).then((response) => {
      commit(types.LIKED_COMMENTS_CACHED, { username })
      response.data.result.comments.forEach((id) => {
        commit(types.LIKED_COMMENT, { id, username, liked: true })
      })
    })
  },
  likeComment ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      comments.like(id).then((response) => {
        commit(types.LIKE_COMMENT, { id })
        commit(types.LIKED_COMMENT, { id, username, liked: true })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  commentLikedBy ({ commit }, { id, limit, offset }) {
    return new Promise((resolve, reject) => {
      comments.getLikes(id, limit, offset).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  unlikeComment ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      comments.unlike(id).then((response) => {
        commit(types.UNLIKE_COMMENT, { id })
        commit(types.LIKED_COMMENT, { id, username, liked: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  likedComment ({ commit, state }, { id, username }) {
    return new Promise((resolve, reject) => {
      if (state.likes[id] && state.likes[id][username] !== undefined) {
        return resolve(state.likes[id][username])
      }

      if (state.likes.cached && state.likes.cached[username]) {
        return resolve(false)
      }

      comments.hasLiked(id, username).then((response) => {
        const liked = response.data.result.liked
        commit(types.LIKED_COMMENT, { id, username, liked })
        resolve(liked)
      }, (error) => {
        reject(error)
      })
    })
  },
  addCommentAttachments ({ commit }, { id, attachments }) {
    return new Promise((resolve, reject) => {
      comments.addAttachment(id, { attachments }).then((response) => {
        const newAttachments = response.data.result.attachments
        newAttachments.forEach((attachment) => {
          commit(types.ADD_COMMENT_ATTACHMENT, attachment)
        })
        resolve(newAttachments)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateCommentAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      comments.updateAttachment(id, attachment.id, attachment).then((response) => {
        const updatedAttachment = response.data.result.attachment
        commit(types.UPDATE_COMMENT_ATTACHMENT, updatedAttachment)
        resolve(updatedAttachment)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteCommentAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      comments.deleteAttachment(id, attachment.id).then((response) => {
        commit(types.DELETE_COMMENT_ATTACHMENT, { id, attachmentID: attachment.id })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  copyCommentAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      comments.copyAttachment(id, attachment.id).then((response) => {
        resolve(response.data.result.storageKey)
      }, (error) => {
        reject(error)
      })
    })
  },
  sortAttachmentsForComment ({ commit }, commentID) {
    commit(types.SORT_ATTACHMENTS_FOR_COMMENT, commentID)
  }
}

const mutations = {
  [types.ADD_COMMENT] (state, comment) {
    Vue.set(state.comments, comment.id, comment)
  },

  [types.ADD_PROJECT_COMMENT] (state, comment) {
    if (!state.projectComments[comment.projectID]) {
      Vue.set(state.projectComments, comment.projectID, [])
    }

    if (state.projectComments[comment.projectID].indexOf(comment.id) >= 0) {
      return
    }

    let firstID = 0
    if (state.projectComments[comment.projectID].length > 0) {
      firstID = state.projectComments[comment.projectID][0]
    }

    if (comment.id > firstID) {
      state.projectComments[comment.projectID].unshift(comment.id)
    } else {
      state.projectComments[comment.projectID].push(comment.id)
    }
  },

  [types.ADD_POST_COMMENT] (state, comment) {
    if (!state.postComments[comment.postID]) {
      Vue.set(state.postComments, comment.postID, [])
    }

    if (state.postComments[comment.postID].indexOf(comment.id) >= 0) {
      return
    }

    state.postComments[comment.postID].push(comment.id)
    state.postComments[comment.postID].sort((a, b) => {
      return b - a
    })
  },

  [types.ADD_REPLY] (state, comment) {
    if (!state.replies[comment.parentID]) {
      Vue.set(state.replies, comment.parentID, [])
    }

    if (state.replies[comment.parentID].indexOf(comment.id) >= 0) {
      return
    }

    let firstID = 0
    if (state.replies[comment.parentID].length > 0) {
      firstID = state.replies[comment.parentID][0]
    }

    if (comment.id < firstID) {
      state.replies[comment.parentID].unshift(comment.id)
    } else {
      state.replies[comment.parentID].push(comment.id)
    }
  },

  [types.DELETE_COMMENT] (state, params) {
    const comment = state.comments[params.id]
    delete state.comments[params.id]

    if (comment.projectID && state.projectComments[comment.projectID]) {
      Vue.set(state.projectComments, comment.projectID, state.projectComments[comment.projectID].filter((id) => id !== comment.id))
    }

    if (comment.postID && state.postComments[comment.postID]) {
      Vue.set(state.postComments, comment.postID, state.postComments[comment.postID].filter((id) => id !== comment.id))
    }

    if (comment.parentID && state.replies[comment.parentID]) {
      Vue.set(state.replies, comment.parentID, state.replies[comment.parentID].filter((id) => id !== comment.id))
    }
  },

  [types.LIKE_COMMENT] (state, params) {
    state.comments[params.id].metadata.likes++
  },

  [types.UNLIKE_COMMENT] (state, params) {
    if (state.comments[params.id].metadata.likes > 0) {
      state.comments[params.id].metadata.likes--
    }
  },

  [types.LIKED_COMMENT] (state, params) {
    if (!state.likes[params.id]) {
      Vue.set(state.likes, params.id, {})
    }

    Vue.set(state.likes[params.id], params.username, params.liked)
  },

  [types.LIKED_COMMENTS_CACHED] (state, params) {
    if (!state.likes.cached) {
      state.likes.cached = {}
    }

    state.likes.cached[params.username] = true
  },

  [types.RESET_ON_LOGOUT] (state) {
    state.comments = {}
    state.likes = {}
    state.projectComments = {}
    state.postComments = {}
    state.replies = {}
  },

  [types.RESET_ON_FEED] (state) {
    state.comments = {}
  },

  [types.UPDATE_COMMENT_REPLY_COUNT] (state, params) {
    if (!state.comments[params.id]) {
      return
    }

    if (params.total) {
      state.comments[params.id].metadata.replies = params.total
    } else {
      state.comments[params.id].metadata.replies += params.count
    }

    if (state.comments[params.id].metadata.replies < 0) {
      state.comments[params.id].metadata.replies = 0
    }
  },

  [types.DELETE_COMMENT_ATTACHMENT] (state, params) {
    if (state.comments[params.id].metadata.attachment > 0) {
      state.comments[params.id].metadata.attachment--
    }

    Vue.set(state.comments[params.id], 'attachments', state.comments[params.id].attachments.filter((attachment) => attachment.id !== params.attachmentID))
  },

  [types.ADD_COMMENT_ATTACHMENT] (state, attachment) {
    if (!state.comments[attachment.commentID]) {
      return
    }

    if (!state.comments[attachment.commentID].attachments) {
      Vue.set(state.comments[attachment.commentID], 'attachments', [])
    }

    state.comments[attachment.commentID].attachments.push(attachment)
  },

  [types.UPDATE_COMMENT_ATTACHMENT] (state, attachment) {
    if (!state.comments[attachment.commentID]) {
      return
    }

    let index = -1
    state.comments[attachment.commentID].attachments.forEach((a, offset) => {
      if (a.id === attachment.id) {
        index = offset
      }
    })

    Vue.set(state.comments[attachment.commentID].attachments, index, attachment)
  },
  [types.SORT_ATTACHMENTS_FOR_COMMENT] (state, commentID) {
    const localAttachments = state.comments[commentID].attachments
    localAttachments.sort(function (a, b) { return a.priority - b.priority })

    state.comments[commentID].attachments = localAttachments
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
